import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CtMetadata } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-metadata';
import {
  LayerThematicStylesDto,
  LayerType,
  MapCssStyleDto,
  MapThematicRangeItemDto,
  MapThematicStylesDto,
  ThematicType,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { MasterOperatorFields } from '@cloud-twin/master-operator/models/master-operator-fields.enum';
import { SetValueAction } from '@common-modules/dynamic-layout/state/generic/generic.actions';
import { StateAreas } from '@common-modules/redux/models/state-areas';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { MapCssStyle } from '@common-modules/wlm-map/generic-map/models/layer-type';
import { GenericMapStylesService } from '@common-modules/wlm-map/generic-map/services/generic-map-styles.service';
import { SpinnerService } from '@common-modules/wlm-spinner/spinner.service';
import Color from 'color';
import { finalize } from 'rxjs';
import { LayerEventsService } from '../../layers-selector/services/layer-events.service';
import { ThematicsService } from '../services/thematics.service';

const COMPONENT_SELECTOR = 'wlm-thematic-configurator-popup';

interface ControlData {
  shape: string;
  Color: string;
}

@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './thematic-configurator-popup.component.html',
  styleUrl: './thematic-configurator-popup.component.scss',
  standalone: false,
})
export class ThematicConfiguratorPopupComponent implements OnInit, OnChanges {
  @Input() isEditMode = false;
  @Input() metadata: CtMetadata;
  @Input() layerType: LayerType;

  private _uom: string;
  @Input()
  set uom(value: string) {
    this._uom = value;
    this.buildSubtitle(value);
  }
  get uom(): string {
    return this._uom;
  }
  private _model: LayerThematicStylesDto;

  @Input()
  get model(): LayerThematicStylesDto {
    return this._model;
  }
  set model(value: LayerThematicStylesDto) {
    if (value) {
      this.processModel(value);
      if (this.uom) {
        this.buildSubtitle(this.uom);
      }
    }
  }

  private _isValid: boolean;
  @Input()
  set isValid(value: boolean) {
    this._isValid = value;
    this.checkValidates(this.isRangesValid, this.isValid);
  }

  get isValid(): boolean {
    return this._isValid;
  }

  @Output() popupClose = new EventEmitter<any>();

  private previousLayerType: LayerType;
  private setLoading: (loading: boolean) => void;
  readonly T_SCOPE = `${AppModules.Map}.${COMPONENT_SELECTOR}`;

  private readonly _layerService = inject(LayerEventsService);
  private readonly _genericMapStylesService = inject(GenericMapStylesService);
  private readonly _state = inject(ReduxStateService);

  rangeDefinition: { value: number; styleDefinition: MapCssStyle }[] = [];
  formControl: FormControl = new FormControl();
  readonly defaultColor = 'rgb(97, 97, 97)';
  numberClasses = 2;
  initialTimelineValue: number = 0;
  showShapeContainer: boolean[] = [];
  controlsData: ControlData[] = [];
  globalOpacity: number = 0;
  knowShape: string;
  subTitle: string;
  isRangesValid: boolean = true;
  allValid: boolean;
  cpCustomizableHeight = 300;
  cpCustomizableWidth = 190;

  constructor(
    private _spinnerService: SpinnerService,
    private readonly _thematicsService: ThematicsService,
    private readonly _dialogService: DialogService,
  ) {
    this.setLoading = this._spinnerService.buildSetLoadingFn();
    const stateWidgetSettings = new StateWidgetSettings({
      module: 'Main',
      page: 'MapPage',
      scopeInstanceKeys: {
        [StateAreas.Generic]: 'shared',
      },
    });

    this._state.configure(stateWidgetSettings);
  }

  ngOnInit() {
    this.initControlsData();
    this.previousLayerType = this.layerType;
  }

  ngOnChanges() {
    if (this.layerType !== this.previousLayerType) {
      this.numberClasses = 2;
      this.initialTimelineValue = 0;
      this.showShapeContainer = [];
      this.controlsData = [];
      this.initControlsData();
      this.previousLayerType = this.layerType;
    } else {
      this.initControlsData();
    }
  }

  onNumberClassesChange() {
    this.initControlsData();
  }

  initControlsData() {
    if (
      !this.model.styleDefinition.rangeDefinition ||
      this.model.styleDefinition.rangeDefinition.length === 0
    ) {
      const oldData = this.controlsData || [];
      this.controlsData = Array(this.numberClasses)
        .fill(null)
        .map((_, idx) => oldData[idx] || { shape: 'circle', Color: this.defaultColor });
      this.showShapeContainer = Array(this.numberClasses).fill(false);
      this.model.styleDefinition.rangeDefinition = Array(this.numberClasses)
        .fill(null)
        .map(
          (_, idx, arr) =>
            new MapThematicRangeItemDto({
              value: idx === arr.length - 1 ? Number.MAX_SAFE_INTEGER : 1,
              styleDefinition: new MapCssStyleDto({
                definition: {
                  Color: this.defaultColor,
                  Size: '10',
                  Opacity: '100',
                  Weight: '3',
                },
              }),
            }),
        );

      this.controlsData.forEach((control, idx) => {
        this.onSelectShape(control.shape, idx);
      });
    }
  }

  onSelectShape(shape: string, idx: number) {
    this.controlsData[idx].shape = shape;

    const color = this.controlsData[idx].Color || this.defaultColor;
    const rangeDef = this.model.styleDefinition.rangeDefinition?.[idx];
    if (rangeDef && rangeDef.styleDefinition && rangeDef.styleDefinition.definition) {
      if (
        this.layerType === LayerType.Point &&
        ['triangulo', 'cuadrado', 'circle', 'rombo'].includes(shape)
      ) {
        rangeDef.styleDefinition.definition.iconPath = this.getShapeSvg(shape, color);
      } else {
        if (rangeDef.styleDefinition.definition.iconPath) {
          delete rangeDef.styleDefinition.definition.iconPath;
        }
      }
    }
  }

  onColorPickerChange(color: string, idx: number, shape?: string) {
    const formattedColor = this.mapToOutputAcceptedFormat(color);
    this.formControl.setValue(formattedColor);
    this.formControl.markAsDirty();

    if (!this.controlsData[idx]) {
      const defaultShape = shape || 'circle';
      this.controlsData[idx] = { shape: defaultShape, Color: formattedColor };
    }
    this.controlsData[idx].Color = formattedColor;

    const rangeDef = this.model.styleDefinition.rangeDefinition?.[idx];
    if (rangeDef && rangeDef.styleDefinition && rangeDef.styleDefinition.definition) {
      const style: MapCssStyle = rangeDef.styleDefinition as any;

      if (this._genericMapStylesService) {
        this._genericMapStylesService.setColorToStyle(style, formattedColor);
      }

      const shape = this.controlsData[idx].shape;
      if (
        this.layerType === LayerType.Point &&
        ['triangulo', 'cuadrado', 'circle', 'rombo'].includes(shape)
      ) {
        rangeDef.styleDefinition.definition.iconPath = this.getShapeSvg(shape, formattedColor);
      } else {
        if (rangeDef.styleDefinition.definition.iconPath) {
          delete rangeDef.styleDefinition.definition.iconPath;
        }
      }
    }
  }

  onTimelineValueChange(newValue: number): void {
    this.initialTimelineValue = newValue;
  }

  get currentOpacity() {
    const rangeDefinitions = this.model?.styleDefinition?.rangeDefinition;
    const firstRange = rangeDefinitions?.length ? rangeDefinitions[0] : null;
    const opacity = firstRange?.styleDefinition?.definition?.Opacity ?? this.globalOpacity;
    return this._genericMapStylesService.mapFromDecimalOpacity(opacity);
  }

  mapToOutputAcceptedFormat = (color: string): string => {
    let colorObject = new Color(color);
    const convertedColor = colorObject.hex();
    return convertedColor;
  };

  onSizeChange(idx: number, size: number) {
    this.model.styleDefinition.rangeDefinition[idx].styleDefinition.definition.Size = String(size);
    if (['Line', 'MultiLine', 'LineString'].includes(this.model.layerType)) {
      this.model.styleDefinition.rangeDefinition[idx].styleDefinition.definition.Weight =
        String(size);
    }
  }

  onOpacityChange(value: number) {
    const opacity = this._genericMapStylesService.mapToDecimalOpacity(value);
    this.globalOpacity = opacity;
    this.model.styleDefinition.rangeDefinition.forEach((rd) => {
      rd.styleDefinition.definition.Opacity = opacity as any;
    });
  }

  onRangeValueChange(index: number, value: number) {
    if (this.model.styleDefinition.rangeDefinition[index]) {
      this.model.styleDefinition.rangeDefinition[index].value = value;
    }
  }

  addRangeDefinition(idx: number) {
    const newItem = new MapThematicRangeItemDto({
      value: 1,
      styleDefinition: new MapCssStyleDto({
        definition: {
          Color: this.defaultColor,
          Size: '10',
          Opacity: this.globalOpacity.toString(),
          Weight: '3',
        },
      }),
    });

    this.model.styleDefinition.rangeDefinition.splice(idx + 1, 0, newItem);
    this.controlsData.splice(idx + 1, 0, { shape: 'circle', Color: this.defaultColor });
    this.showShapeContainer.splice(idx + 1, 0, false);
  }

  deleteRangeDefinition(idx: number) {
    this.model.styleDefinition.rangeDefinition.splice(idx, 1);
    this.controlsData.splice(idx, 1);
    this.showShapeContainer.splice(idx, 1);
  }

  onMaxValueChange(idx: number, value: number) {
    this.model.styleDefinition.rangeDefinition[idx].value = value;
  }

  hasMaxError(idx: number): boolean {
    const min = idx === 0 ? 0 : this.model.styleDefinition.rangeDefinition[idx - 1]?.value;
    const max = this.model.styleDefinition.rangeDefinition[idx]?.value;
    const isEmpty = max === undefined || max === null;
    let error = false;
    if (isEmpty) {
      error = true;
    } else if (max <= min) {
      error = true;
    }
    this.isRangesValid = error;
    this.checkValidates(this.isRangesValid, this.isValid);
    return error;
  }

  checkValidates(isValidRanges: boolean, isValidForm: boolean) {
    this.allValid = !isValidRanges && isValidForm;
  }

  onSave() {
    if (this.isValid) {
      // Ensure all ranges have the correct opacity #26896
      this.onOpacityChange(this.currentOpacity);
      this.checkModelInstances();
      this.setLoading(true);
      this._thematicsService
        .upsertThematic(this.model, this.isEditMode ? this.model.id : undefined)
        .pipe(
          finalize(() => {
            this.setLoading(false);
          }),
        )
        .subscribe({
          next: (result) => {
            this.model.id = result;
            if (!this.isEditMode) {
              this._layerService.setNewThematic(this.model);
              this._dialogService.showEntityActionSnackBar('create', 'thematic.singular');
            } else {
              this._layerService.setEditThematic(this.model);
              this._dialogService.showEntityActionSnackBar('update', 'thematic.singular');
            }

            this.dispatchThemeUpdate();
          },
          error: (error) => {
            this._dialogService.showErrorMessage(error);
          },
        });
    }
  }

  closePopup(): void {
    this.model = undefined;
    this.popupClose.emit();
  }

  isKnownShapeSvg(svg: string, color: string): string | null {
    const shapes = ['circle', 'cuadrado', 'triangulo', 'rombo'];
    for (const shape of shapes) {
      if (svg === this.getShapeSvg(shape, color)) {
        return shape;
      }
    }
    return null;
  }

  private dispatchThemeUpdate() {
    this._state.dispatch(
      new SetValueAction({
        fieldName: MasterOperatorFields.ThematicChanged,
        value: null,
      }),
    );

    this._state.dispatch(
      new SetValueAction({
        fieldName: MasterOperatorFields.ThematicChanged,
        value: true,
      }),
    );
  }

  private processModel(value: LayerThematicStylesDto) {
    if (!value.styleDefinition) {
      value.styleDefinition = new MapThematicStylesDto();
    }

    value.styleDefinition.thematicType = ThematicType.Graduated;

    if (!value.styleDefinition.rangeDefinition) {
      value.styleDefinition.rangeDefinition = [];
    }

    value.styleDefinition.fieldName = value.fieldName;

    this._model = value;

    if (
      this._model.styleDefinition.rangeDefinition &&
      this._model.styleDefinition.rangeDefinition.length > 0
    ) {
      this.globalOpacity = Number(
        this._model.styleDefinition.rangeDefinition[0].styleDefinition.definition.Opacity,
      );

      let sortedRanges = [...this._model.styleDefinition.rangeDefinition].sort(
        (a, b) => (a.value ?? 0) - (b.value ?? 0),
      );

      if (sortedRanges[sortedRanges.length - 1].value !== Number.MAX_SAFE_INTEGER) {
        sortedRanges[sortedRanges.length - 1].value = Number.MAX_SAFE_INTEGER;
      }

      this._model.styleDefinition.rangeDefinition = sortedRanges;
      this.numberClasses = sortedRanges.length;

      this.controlsData = sortedRanges.map((range, idx) => {
        const def = range.styleDefinition?.definition || {};
        let color = def.Color || def.color || this.defaultColor;
        let shape = def.shape || 'circle';

        this.initialTimelineValue = Number(def.Size || def.size || 0);

        if (def.iconPath) {
          const knownShape = this.isKnownShapeSvg(def.iconPath, color);
          if (knownShape) {
            shape = knownShape;
          }
        }

        if (color) {
          setTimeout(() => this.onColorPickerChange(color, idx, shape), 0);
        }

        return {
          shape,
          Color: color,
          color,
        };
      });

      this.showShapeContainer = Array(this.numberClasses).fill(false);
    }
  }

  private checkModelInstances() {
    if (!(this.model.styleDefinition instanceof MapThematicStylesDto)) {
      this.model.styleDefinition = new MapThematicStylesDto(this.model.styleDefinition);
    }
    if (Array.isArray(this.model.styleDefinition.rangeDefinition)) {
      this.model.styleDefinition.rangeDefinition = this.model.styleDefinition.rangeDefinition.map(
        (item) => {
          const rangeItem =
            item instanceof MapThematicRangeItemDto ? item : new MapThematicRangeItemDto(item);
          if (!(rangeItem.styleDefinition instanceof MapCssStyleDto)) {
            rangeItem.styleDefinition = new MapCssStyleDto(rangeItem.styleDefinition);
          }
          return rangeItem;
        },
      );
    }
  }

  private getShapeSvg(shape: string, color: string): string {
    switch (shape) {
      case 'triangulo':
        return `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15"><polygon points="7.5,0 0,15 15,15" fill="${color}" /></svg>`;
      case 'cuadrado':
        return `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15"><rect x="0" y="0" width="15" height="15" fill="${color}"/></svg>`;
      case 'circle':
        return `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15"><circle cx="7.5" cy="7.5" r="7.5" fill="${color}" /></svg>`;
      case 'rombo':
        return `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15"><polygon points="7.5,0 15,7.5 7.5,15 0,7.5" fill="${color}" /></svg>`;
      default:
        return '';
    }
  }

  private buildSubtitle(value: string) {
    this.subTitle = `${this.model.fieldName} (${value})`;
  }
}
